import type { ShapeData } from '@/lib/shapes/types/shape-data'
import { rotatedVisualAabbOfShape } from '@/lib/shapes/shape-coordinates'

/**
 * Axis-aligned canvas bbox of the shape's rotated *visual* box — the basis for the left panel's
 * X/Y (its min corner), and for align/distribute/paste, which share this function.
 *
 * Computed from `ShapeData` alone (`rotatedVisualAabbOfShape`), not measured. This used to delegate
 * to `canvasAabbOfRotatedPathBounds` with a `getBBox()`-measured path-bounds argument, which had a
 * real correctness bug: at ~0° rotation it discarded the measured tight bounds entirely and
 * substituted the raw placement frame, but honored the tight bounds at any other angle. For an inset
 * shape (triangle, star, regular polygon) that meant the panel's X/Y — and every align/distribute
 * target and paste offset computed from it — jumped discontinuously the instant the shape left
 * exactly 0° rotation. `rotatedVisualAabbOfShape` has no such special case; it is the same box at
 * every angle, including 0°. The staleness guard this module used to need
 * (`coherentPathBoundsForPanel`, dropped) existed only to cover a measured value trailing the
 * shape's own placement after a resize — a problem a computed value cannot have.
 */
export function canvasAabbForShapePanel(shape: ShapeData): {
  minX: number
  minY: number
  maxX: number
  maxY: number
} {
  const aabb = rotatedVisualAabbOfShape(shape)

  return { minX: aabb.x, minY: aabb.y, maxX: aabb.x + aabb.width, maxY: aabb.y + aabb.height }
}

export function canvasMinForSingleShapePanel(shape: ShapeData): { minX: number; minY: number } {
  const abb = canvasAabbForShapePanel(shape)

  return { minX: abb.minX, minY: abb.minY }
}
